import Big from 'big.js'

import { AtticScope, CellarScope, MeasurementOnSitePropertyType, isAtticScopeMeasured } from 'models/purchaseFlow/measurementDTO'
import { MeasurementQuoteLineItemDTO, MeasurementQuoteLineItemType } from 'models/purchaseFlow/measurementQuoteDTO'
import { FeeDTO } from 'models/fee'
import { MeasurementPricingDTO } from 'models/purchaseFlow/measurementPricingDTO'
import { bigFromFee } from 'utils/price/decimals'

/**
 * Price hints show the rate card's own amounts (GET /order/configuration/measurement/pricing), so a pill always
 * promises the amount that will appear on the matching cost-panel line.
 *
 * They used to show the net DELTA of the change against the whole configuration (ISSUE-2702). That folded the
 * engine's Kat. 1 fallback into the hint: on a single-floor house the attic pill promised €141 while the panel
 * billed the attic at €245, and the "2nd floor" hint promised €141 against a €245 price list (ISSUE-2861).
 *
 * What is left here are the two rules the rate card cannot resolve on its own — which of the two attic rates
 * applies, and (Kat. 2 only) which of the two cellar rates. Both mirror MeasurementQuoteService in
 * backend-platform; keep them in sync.
 */

export interface MeasurementLevelConfig {
  propertyType: MeasurementOnSitePropertyType
  /** Floors above ground — for the unit-based types the sum over all residential units */
  totalFloors: number
  cellarScope: CellarScope
}

/**
 * Rate for the attic at its position in the building: the 2nd-level rate only when it sits directly above a
 * single above-ground floor with nothing *charged* below it — a free cellar room is measured but does not
 * count as a level (ISSUE-2882) — the 3rd+-level rate otherwise. Kat. 1 is the exception: an apartment's
 * attic is always the additional-floor rate, it has no 2nd-level attic (epic §3, ISSUE-3016).
 *
 * Mirrors MeasurementQuoteService.atticCarrier (backend-platform, ISSUE-2882); findHintQuoteDivergences
 * reports any drift between the two.
 */
export const atticLevelRate = (config: MeasurementLevelConfig, pricing: MeasurementPricingDTO): Big => {
  // Written as the engine's atticCarrier, positively keyed on the House: the 2nd-level attic exists only
  // there — apartments (ISSUE-3016) and the unit-based categories always take the additional-floor rate
  const isSecondLevel = config.propertyType === MeasurementOnSitePropertyType.SINGLE_FAMILY_HOUSE
    && config.totalFloors === 1
    && config.cellarScope !== CellarScope.WHOLE

  return isSecondLevel ? bigFromFee(pricing.atticSecondLevel) : bigFromFee(pricing.atticThirdPlusLevel)
}

/**
 * Rate for a whole cellar. Above-ground floors take the building's first positions and the cellar the next one,
 * so on a 2+-floor House the cellar sits at the 3rd level or higher and carries the cheaper level rate; on a
 * single-floor property it is effectively the 2nd floor and keeps the flat rate.
 *
 * Kat. 2 only (decided 2026-08-08): a House prices its cellar by level, every other category keeps the flat
 * rate. Mirrors MeasurementQuoteService's cellar carrier pick — the backend carrier is
 * EXTRA_MEASURED_HOUSE_BASEMENT_SECOND_FLOOR (the SF code says SECOND_FLOOR but it prices the 3rd+ level).
 * Falls back to the flat rate whenever the backend has not sent the carrier for this country yet.
 */
export const cellarWholeRate = (config: MeasurementLevelConfig, pricing: MeasurementPricingDTO): Big => {
  const isCellarThirdPlusLevel = config.propertyType === MeasurementOnSitePropertyType.SINGLE_FAMILY_HOUSE
    && config.totalFloors >= 2

  return isCellarThirdPlusLevel && pricing.cellarThirdPlusLevel
    ? bigFromFee(pricing.cellarThirdPlusLevel)
    : bigFromFee(pricing.cellar)
}

/** Add-ons whose card promised a rate-card amount for this quote (ISSUE-2838) */
export interface MeasurementAddOnSelection {
  expressService?: boolean
  additionalHouseViews?: boolean
}

/** A hint rate that no longer matches the engine's own unit price for the same line */
export interface HintQuoteDivergence {
  lineItemType: MeasurementQuoteLineItemType
  hintAmount: string
  quoteAmount: string
}

/**
 * Tripwire for the two rules above: both mirror MeasurementQuoteService by hand, and the mirror has
 * silently drifted twice (ISSUE-2861, ISSUE-3007) — each time promising a pill amount the engine then
 * billed differently. The quote's line items carry the engine's own unit prices for the same
 * configuration, so compare them against the hint rates and report what no longer matches. Lines the
 * engine prices at €0 by scope (a free cellar room, a present-but-unmeasured level) carry no comparable
 * unit rate and are skipped.
 */
export const findHintQuoteDivergences = (
  config: MeasurementLevelConfig,
  pricing: MeasurementPricingDTO,
  lineItems: MeasurementQuoteLineItemDTO[],
  atticScope: AtticScope,
  addOns: MeasurementAddOnSelection = {}
): HintQuoteDivergence[] => {
  const comparisons = [
    {
      lineItemType: MeasurementQuoteLineItemType.CELLAR,
      isComparable: config.cellarScope === CellarScope.WHOLE,
      hintRate: () => cellarWholeRate(config, pricing)
    },
    {
      lineItemType: MeasurementQuoteLineItemType.ATTIC,
      isComparable: isAtticScopeMeasured(atticScope),
      hintRate: () => atticLevelRate(config, pricing)
    },
    // Flat add-on fees (ISSUE-2838): no level rule to mirror, but the card's promise can still drift from the engine's unit price
    {
      lineItemType: MeasurementQuoteLineItemType.EXPRESS_SERVICE,
      isComparable: !!addOns.expressService && !!pricing.expressService,
      hintRate: () => bigFromFee(pricing.expressService as FeeDTO)
    },
    {
      lineItemType: MeasurementQuoteLineItemType.ADDITIONAL_HOUSE_VIEWS,
      isComparable: !!addOns.additionalHouseViews && !!pricing.additionalHouseViews,
      hintRate: () => bigFromFee(pricing.additionalHouseViews as FeeDTO)
    }
  ]

  return comparisons.flatMap(({ lineItemType, isComparable, hintRate }) => {
    const unitPrice = isComparable ? lineItems.find(item => item.type === lineItemType)?.unitPrice : null
    if (!unitPrice) return []

    const hint = hintRate()
    const quote = bigFromFee(unitPrice)

    return hint.eq(quote)
      ? []
      : [{ lineItemType, hintAmount: hint.toString(), quoteAmount: quote.toString() }]
  })
}
